import type { BoxProps } from '@/components/Box';
import { Col } from '@/components/Col';
import type { AddressFragment } from '@/graphql/saleor/api';
import { CONTENTSQUARE_MASKED } from '@/lib/forms/const';
import { generateDataTestId } from '@/lib/tests';
import type { Maybe, WithChildren } from '@/lib/types';

import { formatAddress } from '../MyAddresses/AddressSummary';

import * as S from './Summary.styles';

type SummaryProps = WithChildren & { name: string };

const Summary = ({ children, name }: SummaryProps) => {
  return (
    <Col
      {...generateDataTestId('section', name)}
      data-test-id={`Summary${name}`}
    >
      {children}
    </Col>
  );
};

type SectionProps = WithChildren & { className?: string };

const Section = ({ children, className }: SectionProps) => (
  <S.Section className={className} data-test-id='SummarySection'>
    {children}
  </S.Section>
);

const Entry = ({ children }: WithChildren) => (
  <S.Entry data-test-id='SummaryEntry'>{children}</S.Entry>
);

type HeaderProps = WithChildren & Partial<Pick<BoxProps, 'onClick'>>;

const Header = ({ children, onClick }: HeaderProps) => (
  <S.HeaderWrapper data-test-id='HeaderWrapper'>
    <S.Header data-test-id='Header'>{children}</S.Header>
    <S.IconWrapper
      data-test-id='IconWrapper'
      disabled={!onClick}
      onClick={onClick}
    >
      <S.EditIcon data-test-id='iconEdit' />
    </S.IconWrapper>
  </S.HeaderWrapper>
);

export interface LineProps extends WithChildren {
  className?: string;
  isBold?: boolean;
}

export const Line = ({ children, className, isBold }: LineProps) => {
  return (
    <S.Line className={className} data-test-id='Line' isBold={isBold}>
      {children}
    </S.Line>
  );
};

Summary.Header = Header;
Summary.Line = Line;
Summary.Section = Section;
Summary.Entry = Entry;

const renderAddress = (
  honorifics: Maybe<string>,
  renderDeliverySchedule: boolean,
  header: string,
  address: Maybe<AddressFragment>,
  deliverySchedule?: {
    header: string;
    val: string;
  },
  hideName = false,
) => (
  <Summary.Section className={CONTENTSQUARE_MASKED}>
    <Summary.Entry>
      <Summary.Line data-test-id='LineHeader' isBold>
        {header}
      </Summary.Line>
      {!hideName && (
        <Summary.Line data-test-id='LineName'>
          {honorifics}
          {address?.firstName} {address?.lastName}
        </Summary.Line>
      )}
      {address?.companyName && (
        <Summary.Line data-test-id='LineCompanyName'>
          {address.companyName}
        </Summary.Line>
      )}
      <Summary.Line data-test-id='LineAddress'>
        {formatAddress(address as AddressFragment, address?.country?.code)}
      </Summary.Line>
    </Summary.Entry>
    {deliverySchedule && renderDeliverySchedule && (
      <Summary.Entry>
        <Summary.Line data-test-id='LineDeliveryHeader' isBold>
          {deliverySchedule.header}
        </Summary.Line>
        <Summary.Line data-test-id='LineDeliveryVal'>
          {deliverySchedule.val}
        </Summary.Line>
      </Summary.Entry>
    )}
  </Summary.Section>
);

export { Summary, renderAddress };
